import { useQuery } from "@tanstack/react-query";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useCallback, useState } from "react";
import { If } from "../../../../components/If/If";
import { classNames as cx } from "primereact/utils";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";
import { Loader } from "../../../../components/Loader/Loader";
import { SectionHeader } from "../../../../components/SectionHeader/SectionHeader";
import { debounce } from "../../../../helpers/debounce";
import { renderDateColumn } from "../../../../helpers/renderDateColumn";
import { useFilterQuery } from "../../../../hooks/useFilterQuery";
import { requests } from "../../../../services/http-common";
import { IFilter } from "../../../../types";
import { ContractStatusEnum } from "../Contracts.model";

import classes from "./ContractsList.module.scss";
import { Actions } from "./columns/contract-actions";
import { ContractStatusCell } from "./columns/contract-status";
import {
  countContractsDueForRenewal,
  getRenewalDisplay,
} from "./columns/renewal";
import { CustomerCategoryEnum } from "../../SalesLead/Lead.model";
import { Status } from "./columns/Status/Status";
import { PrivateAccess, useUser } from "../../../../keycloak";
import { UserRoles } from "../../../../constants/userRoles";
import { useEffectWithoutMount } from "../../../../helpers/helperHooks";
import { EmployeeRoles } from "../../../../models/Employee";
import { Button } from "primereact/button";
import { CreateContractModal } from "./CreateJobModal/CreateContractModal";
import { ContractImportExport } from "../../../../components/contract-import-export";

const scheduledOptions = [
  { label: "All", value: "" },
  { label: "Scheduled", value: "scheduled" },
  { label: "Not scheduled", value: "unscheduled" },
];

const statusOptions = [
  {
    label: "All",
    value: "",
  },
  {
    label: "Pending",
    value: ContractStatusEnum.Pending,
  },
  {
    label: "Draft",
    value: ContractStatusEnum.Draft,
  },
  {
    label: "Approved",
    value: ContractStatusEnum.Approved,
  },
  {
    label: "Sent",
    value: ContractStatusEnum.Sent,
  },
  {
    label: "Denied",
    value: ContractStatusEnum.Denied,
  },
  {
    label: "Completed",
    value: ContractStatusEnum.Completed,
  },
];

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByStatus: "",
  filterByScheduled: "",
  filterByCustomerCategory: "",
  filterByProjectType: "",
  sortOrder: -1,
};

export const ContractsList = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [term, setTerm] = useState("");
  const filterQuery = useFilterQuery(filters);
  const [showCreateContractModal, setShowCreateContractModal] =
    useState<boolean>(false);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: [`contract-list`],
    queryFn: () => {
      return requests.get(`/contract?${filterQuery}`);
    },
    retry: false,
    cacheTime: 0,
  });

  const user: any = useUser();

  useEffectWithoutMount(() => {
    refetch();
  }, [filters, refetch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearch = useCallback(
    debounce((event: any) => {
      setFilters((prev) => ({ ...prev, filter: event.target.value }));
    }, 500),
    [],
  );

  const handleInputSearch = (e: any) => {
    debouncedOnSearch(e);
    setTerm(e.target.value);
  };

  const onDropdownChange = (e: DropdownChangeParams) => {
    setFilters((prev) => ({ ...prev, [e.target.id]: e.value }));
  };

  const renewalDueCount = countContractsDueForRenewal(data?.result);

  const onPage = useCallback(
    (event: DataTablePFSEvent) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters],
  );

  return (
    <>
      {showCreateContractModal ? ( // prime react modal always mounts, thats why so
        <CreateContractModal
          set={setShowCreateContractModal}
          show={showCreateContractModal}
        />
      ) : null}
      <div className={classes.container}>
        <SectionHeader title="Contracts" />
        <div className={classes.headerActions}>
          <PrivateAccess
            roles={{
              dcs_client: [UserRoles.CONTRACT_CREATE],
            }}
          >
            {user && user.role && user.role !== "CLIENT" ? (
              <Button
                label="Create Contract"
                className={cx("p-button-sm", classes.createBtn)}
                onClick={() => setShowCreateContractModal(true)}
              />
            ) : null}
          </PrivateAccess>
          <ContractImportExport
            exportUrl={`/contract/export-csv?${filterQuery}`}
            importUrl="/contract/import-csv"
            refetchQuery={refetch}
            showExport
          />
        </div>
        {renewalDueCount > 0 ? (
          <div className={classes.renewalBanner}>
            <i className="pi pi-exclamation-circle" />
            <span>
              {renewalDueCount} contract{renewalDueCount === 1 ? "" : "s"} need
              {renewalDueCount === 1 ? "s" : ""} renewal in the next 60 days.
            </span>
          </div>
        ) : null}
        <div className={classes.filters}>
          <span className={cx("p-input-icon-left", classes.search)}>
            <i className="pi pi-search" />
            <InputText
              onChange={handleInputSearch}
              value={term}
              placeholder="Search"
            />
          </span>
          <div className={classes.filterGroup}>
            <LabelValue label="Select Status">
              <Dropdown
                appendTo="self"
                options={statusOptions}
                id="filterByStatus"
                onChange={onDropdownChange}
                value={filters.filterByStatus}
                className={classes.dropdown}
              />
            </LabelValue>
            <LabelValue label="Scheduled">
              <Dropdown
                appendTo="self"
                options={scheduledOptions}
                id="filterByScheduled"
                onChange={onDropdownChange}
                value={filters.filterByScheduled}
                className={classes.dropdown}
              />
            </LabelValue>
          </div>
        </div>
        <If condition={isLoading}>
          <Loader />
          <div className={classes.table}>
            <DataTable
              value={data?.result}
              responsiveLayout="scroll"
              paginator={true}
              paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
              first={filters.first}
              rows={filters.rows}
              totalRecords={data?.total}
              onPage={onPage}
              paginatorPosition="top"
              paginatorClassName={classes.paginator}
              lazy
            >
              <Column field="contractN" header="Contract N" />
              <Column field="contractName" header="Contract Name" />
              {user?.role !== EmployeeRoles.CLIENT ? (
                <Column
                  header="Client Name"
                  body={(rowData: any) => {
                    return (
                      <span>
                        {rowData.lead.customerCategory ===
                        CustomerCategoryEnum.CORPORATE
                          ? `${rowData.lead.contactName}`
                          : `${rowData.lead.name ?? ""} ${
                              rowData.lead.surname ?? ""
                            }`}
                      </span>
                    );
                  }}
                />
              ) : null}
              {user?.role !== EmployeeRoles.CLIENT ? (
                <Column
                  field="creator"
                  header="Created By"
                  body={(rowData) => (
                    <span>{`${rowData.creator.firstName} ${rowData.creator.lastName}`}</span>
                  )}
                />
              ) : null}
              {user?.role !== EmployeeRoles.CLIENT ? (
                <Column
                  field="createdAt"
                  header="Created At"
                  body={(rowData) => renderDateColumn(rowData.createdAt)}
                />
              ) : null}
              {user?.role !== EmployeeRoles.CLIENT ? (
                <Column
                  field="updatedAt"
                  header="Updated At"
                  body={(rowData) => renderDateColumn(rowData.updatedAt)}
                />
              ) : null}
              <Column
                header="Contract Status"
                body={(rowData) => <ContractStatusCell rowData={rowData} />}
              />
              <Column
                header="Renewal"
                body={(rowData) => {
                  const renewal = getRenewalDisplay(rowData);

                  if (!renewal) {
                    return null;
                  }

                  return (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.35rem",
                        color:
                          renewal.kind === "overdue"
                            ? "var(--red-600, #dc2626)"
                            : "var(--orange-500, #f59e0b)",
                      }}
                    >
                      <i className="pi pi-clock" />
                      {renewal.text}
                    </span>
                  );
                }}
              />
              <Column
                field="status"
                body={(rowData: {
                  status: ContractStatusEnum;
                  id: string;
                  sendStatus: boolean;
                }) => (
                  <PrivateAccess
                    roles={{
                      dcs_client: [UserRoles.CONTRACT_VIEW],
                    }}
                  >
                    <Status {...rowData} />
                  </PrivateAccess>
                )}
                header="Status"
              />
              <Column
                header="Actions"
                body={(rowData: {
                  id: string;
                  status: ContractStatusEnum;
                  lead: { id: string };
                  hasJob: boolean;
                  sendEstimateStatus: boolean;
                  addEstimateStatus: boolean;
                  sendStatus: boolean;
                  isDisabled: boolean;
                }) => <Actions {...rowData} isDisabled={isFetching} />}
              />
            </DataTable>
          </div>
        </If>
      </div>
    </>
  );
};
